<template>
  <div v-if="challenge">
    <HeaderTitle :back-target-func="goToChallenge">
      <template #top>{{ t("comp.challenge.teams.title") }}</template>
      <template #bottom>{{ t("comp.challenge.teams.subtitle") }}</template>
    </HeaderTitle>
    <div class="w-full">
      <SearchInput
        v-model="searchTerm"
        input-classes="input-primary !w-full !border-0 !pl-12 !p-0"
        :placeholder="t('feed.search.placeholder')"
        @on-key-up-enter="searchTeams"
        @on-close-search="closeSearch"
      />
    </div>
    <div
      v-if="searchedTeams.length <= 0 && searchTerm && searchTerm.length > 0"
      class="flex justify-center mt-4"
    >
      {{ t("comp.challenge.teams.emptysearch") }}
    </div>
    <div
      v-else-if="searchedTeams.length > 0"
      class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8"
    >
      <div v-for="team in searchedTeams" :key="team.id" class="w-full">
        <TeamTile
          :id="team.id"
          :name="team.name"
          :size="team.memberCount"
          :max-member="challenge.maxMembers"
          :challenge-id="challenge.mappingId"
        />
      </div>
    </div>
    <div v-else class="text-center w-full mt-8">
      {{ t("comp.challenge.teams.empty") }}
    </div>
    <div class="mt-16 text-center">
      <BaseButton @click="createTeamModalOpen = true">{{
        t("comp.challenge.teams.create.dialog.titlte")
      }}</BaseButton>
    </div>
    <CreateTeamModal
      v-if="!isCurrentUserInAnyTeam"
      :open="createTeamModalOpen"
      :challenge-id="challenge.mappingId"
      @close="createTeamModalOpen = false"
    />
  </div>
</template>
<script setup lang="ts">
import HeaderTitle from "../../../components/v2/layout/header/HeaderTitle.vue";
import { NAVIGATION_CATEGORY_CHALLENGES } from "../../../enum/v2/navigationCategoryType";
import { useNavbarStore } from "../../../store/v2/navbar";
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { fetchCompetitiveChallenge } from "../../../api/v2/competitive-challenge.ts";
import { useUserStore } from "../../../store/v2/user";
import { useClientStore } from "../../../store/v2/client";
import { CompetitiveChallengeDetailsDto } from "../../../dto/competitive-challenge-details.dto.ts";
import SearchInput from "../../../components/v2/layout/SearchInput.vue";
import { CompetitiveChallengeTeamDto } from "../../../dto/competitive-challenge-team.dto.ts";
import TeamTile from "../../../components/v2/competitiveChallenge/TeamTile.vue";
import BaseButton from "../../../components/v2/layout/button/BaseButton.vue";
import CreateTeamModal from "../../../components/v2/competitiveChallenge/CreateTeamModal.vue";
const { t } = useI18n();

const route = useRoute();
const router = useRouter();
const navbarStore = useNavbarStore();
const userStore = useUserStore();
const clientStore = useClientStore();

const challenge = ref<CompetitiveChallengeDetailsDto>(null);
const teams = ref<CompetitiveChallengeTeamDto[]>([]);
const searchedTeams = ref<CompetitiveChallengeTeamDto[]>([]);
const searchTerm = ref(null);
const isCurrentUserInAnyTeam = ref(false);
const createTeamModalOpen = ref(false);

onBeforeMount(() => {
  navbarStore.setActiveNavigationCategory(NAVIGATION_CATEGORY_CHALLENGES);
});

onMounted(async () => {
  const challengeId = route.params.challengeId as string;
  const res = await fetchCompetitiveChallenge(
    userStore.externalUserId,
    clientStore.code,
    challengeId
  );
  const challengeData = res.data;

  if (challengeData) {
    challenge.value = challengeData;
    teams.value = challengeData.competitiveChallengeTeams || [];
    searchedTeams.value = teams.value;
  }

  isCurrentUserInAnyTeam.value = teams.value.some(
    (team) => team.isTeamOfCurrentUser === true
  );

  if (isCurrentUserInAnyTeam.value) {
    router.push({
      name: "competitiveChallengeDetail",
      params: { challengeId: route.params.challengeId },
    });
  }
});

function goToChallenge() {
  router.push({
    name: "competitiveChallengeDetail",
    params: { challengeId: route.params.challengeId },
  });
}

function searchTeams(): void {
  if (!searchTerm.value) {
    searchedTeams.value = teams.value;
    return;
  }

  const term = searchTerm.value.toLowerCase();
  searchedTeams.value = teams.value.filter((team) =>
    team.name?.toLowerCase().includes(term)
  );
}

function closeSearch() {
  searchTerm.value = null;
  searchedTeams.value = teams.value;
}
</script>
